import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AppIconComponent } from '../../../common/components/icon/icon.component';
import { QuotesService } from '../../../core/data/services/quotes.service';
import { QuoteResponseDto } from '../../../core/data/dtos/quote-response.dto';
import { QuoteDetailDto } from '../../../core/data/dtos/quote-detail.dto';
import { QuoteSummaryDto } from '../../../core/data/dtos/quote-summary.dto';
import { beneficiaryRelationshipLabel } from '../../../core/data/constants/beneficiary-relationships';

@Component({
    selector: 'app-quote-success',
    standalone: true,
    imports: [CommonModule, RouterModule, AppIconComponent],
    templateUrl: './quote-success.component.html',
    styleUrls: ['./quote-success.component.css']
})
export class QuoteSuccessComponent implements OnInit {
    readonly relationshipLabel = beneficiaryRelationshipLabel;

    private quotesService = inject(QuotesService);
    private router = inject(Router);

    currentQuote: QuoteResponseDto | null = null;
    quoteSummary: QuoteSummaryDto | null = null;

    ngOnInit(): void {
        this.currentQuote = this.quotesService.currentQuote;
        if (!this.currentQuote) {
            this.router.navigate(['/']);
            return;
        }

        // Fetch summary if not already present.
        // The backend always returns a Summary object (initialized with new()),
        // so we must check for meaningful data (non-zero premium) rather than
        // just checking if the object exists.
        const hasRealSummary = this.currentQuote.summary
            && ((this.currentQuote.summary.totalGrossPremium ?? 0) > 0
                || (this.currentQuote.summary.totalNetPremium ?? 0) > 0);
        if (hasRealSummary) {
            this.quoteSummary = this.currentQuote.summary ?? null;
        } else if (this.currentQuote.quoteId) {
            this.quotesService.getQuoteSummary(this.currentQuote.quoteId, 1).subscribe({
                next: (s) => this.quoteSummary = s,
                error: () => this.quoteSummary = null
            });
        }
    }

    get summary() {
        return this.quoteSummary;
    }

    get details(): QuoteDetailDto[] {
        return this.currentQuote?.details || [];
    }

    get quoteCurrency(): string {
        const d = this.details[0];
        if (d?.currency) {
            const cur = d.currency.toString().trim().toUpperCase();
            return cur === 'USD' ? 'USD$' : 'RD$';
        }
        return 'RD$';
    }

    detailPlanName(detailId: number | undefined): string {
        const detail = this.details.find(d => d.id === detailId);
        return detail?.insuranceType?.name || 'Plan';
    }

    onGoHome(): void {
        this.quotesService.clearCurrentQuote();
        this.router.navigate(['/']);
    }
}
